import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Minus, Check } from "lucide-react";
import { adjustInventory, type StockItem } from "@/api/stock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function StockLevelEditor({ item }: { item: StockItem }) {
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (d: { delta: number; note?: string }) =>
      adjustInventory(item.sku, d.delta, d.note),
    onSuccess: () => {
      setDelta("");
      setNote("");
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["inventory-stock"] });
      // Katalog-Übersicht zeigt eine Bestand-Spalte und liest den Key
      // ["catalog-grundartikel"] (Catalog.tsx). Der frühere Key ["catalog-stock"]
      // wird von keiner Query mehr verwendet → war ein stiller No-op.
      void queryClient.invalidateQueries({ queryKey: ["catalog-grundartikel"] });
      void queryClient.invalidateQueries({ queryKey: ["stock-history", item.sku] });
    },
  });

  function applyDelta(d: number) {
    if (mutation.isPending) return;
    mutation.mutate({ delta: d });
  }

  function applyCustom() {
    const n = Number(delta);
    if (!Number.isFinite(n) || n === 0) return;
    mutation.mutate({ delta: n, note: note.trim() || undefined });
  }

  if (!open) {
    return (
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="size-7 p-0"
          onClick={() => applyDelta(-1)}
          disabled={mutation.isPending || item.quantity_total <= 0}
          title="-1"
        >
          <Minus className="size-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="size-7 p-0"
          onClick={() => applyDelta(1)}
          disabled={mutation.isPending}
          title="+1"
        >
          <Plus className="size-3" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-7 px-2"
          onClick={() => setOpen(true)}
        >
          ±n …
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        value={delta}
        onChange={(e) => setDelta(e.target.value)}
        placeholder="Δ"
        className="w-16 h-7 text-xs font-mono"
        autoFocus
      />
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Notiz"
        className="w-32 h-7 text-xs"
      />
      <Button
        size="sm"
        className="size-7 p-0"
        onClick={applyCustom}
        disabled={mutation.isPending || !delta || delta === "0"}
        title="Anwenden"
      >
        <Check className="size-3" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="size-7 p-0 text-xs"
        onClick={() => {
          setOpen(false);
          setDelta("");
          setNote("");
        }}
      >
        ×
      </Button>
    </div>
  );
}
